import {
  type AuthRequest,
  type AuthResponse,
  AuthService,
  type ConfirmEmailRequest,
  ProfileService,
} from '@/@types/generated'
import { axiosInstance } from '@/@types/generated/core/request'
import { useError } from '@/hooks/useError'
import { getValidationErrors } from '@/lib/siteFunctions'
import type { AxiosError, AxiosResponse } from 'axios'
import {
  createContext,
  PropsWithChildren,
  use,
  useCallback,
  useEffect,
  useState,
} from 'react'

/**
 * Model-state key AuthController.ConfirmEmail uses for a link that is past its 24h lifespan or
 * whose token was superseded by a security-stamp rotation. Kept in sync with
 * `AuthController.ConfirmEmailLinkExpired`, which is asserted by its literal value in
 * AuthControllerConfirmEmailTests so a rename on the server fails the build.
 */
export const CONFIRM_EMAIL_LINK_EXPIRED = 'confirmEmailLinkExpired'

/**
 * Model-state key for an address that is already confirmed. Kept in sync with
 * `AuthController.ConfirmEmailAlreadyConfirmed`.
 */
export const CONFIRM_EMAIL_ALREADY_CONFIRMED = 'confirmEmailAlreadyConfirmed'

/**
 * Outcomes the confirmation page branches on. `invalid` is a malformed link (missing query
 * params) rather than a dead one — resending won't help, so it must not be folded into `expired`.
 */
export type ConfirmEmailOutcome =
  | 'confirmed'
  | 'already-confirmed'
  | 'expired'
  | 'invalid'
  | 'error'

export interface AuthContextType {
  user: AuthResponse | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (credentials: AuthRequest) => Promise<void>
  logout: () => Promise<void>
  refreshAuth: () => Promise<AuthResponse | null>
  confirmEmail: () => Promise<ConfirmEmailOutcome>
  confirmNewEmail: () => Promise<void>
  confirmNewSecondaryEmail: () => Promise<void>
}

// Default auth context for use outside of providers
const AuthContext = createContext<AuthContextType>(undefined!)

export const useAuth = (): AuthContextType => {
  const context = use<AuthContextType>(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthContext')
  }
  return context
}

const AuthProvider = ({ children }: PropsWithChildren) => {
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [user, setUser] = useState<AuthResponse | null>(null)
  const { showSuccess, showError } = useError()

  // Use search params from initial location, useSearchParams()
  // cannot be used outside of react-router
  const searchParams = new URLSearchParams(location.search)
  const code = searchParams.get('code')
  const email = searchParams.get('email')
  const userId = searchParams.get('userId')

  const _login = useCallback(
    async (credentials: AuthRequest) => {
      try {
        const loginUser = await AuthService.postApiAuthLogin(credentials)
        setUser(loginUser)
        showSuccess('Welcome back!', 'Login Successful')
      } catch (e: any) {
        setUser(null)
        showError(e, 'Unable to login')
      }
    },
    [showError, showSuccess]
  )

  const _logout = useCallback(async () => {
    try {
      setIsAuthenticating(true)
      await AuthService.postApiAuthLogout()
    } catch (e: any) {
      showError(e, 'Unable to logout. Please try again.')
    } finally {
      setUser(null)
      setIsAuthenticating(false)
    }
  }, [showError])

  const _checkAuthStatus = useCallback(async () => {
    try {
      setIsAuthenticating(true)
      const user = await AuthService.getApiAuthProfile()
      setUser(user)
      return user
    } catch (e: any) {
      // Silent fail for auth check
      return null
    } finally {
      setIsAuthenticating(false)
    }
  }, [])

  const _confirmEmail = useCallback(async (): Promise<ConfirmEmailOutcome> => {
    try {
      setIsAuthenticating(true)

      if (!userId || !code) return 'invalid'

      const response = await AuthService.getApiAuthConfirmEmail(userId, code)
      setUser(response)

      showSuccess('Thank you, your email has been confirmed.')
      return 'confirmed'
    } catch (e: any) {
      // A dead confirmation link is the expected outcome of clicking an old email — the page
      // renders a recovery state for it rather than a toast plus a redirect. No setUser and no
      // success toast on these paths: the server didn't sign anyone in, so claiming otherwise
      // would leave the app rendering authenticated UI with no session behind it.
      const validationErrors = getValidationErrors(e)
      if (CONFIRM_EMAIL_ALREADY_CONFIRMED in validationErrors)
        return 'already-confirmed'
      if (CONFIRM_EMAIL_LINK_EXPIRED in validationErrors) return 'expired'

      console.error('Error confirming email:', e)
      showError(e, 'Error confirming email')
      return 'error'
    } finally {
      setIsAuthenticating(false)
    }
  }, [code, showError, showSuccess, userId])

  const _confirmNewEmail = useCallback(async () => {
    try {
      setIsAuthenticating(true)
      if (!userId || !email || !code) return

      await ProfileService.postApiProfileConfirmEmail({
        userId,
        email,
        code,
      } as ConfirmEmailRequest)
      showSuccess('Email updated successfully')
      // Intentionally not calling setUser here — the confirming browser isn't signed in as a
      // result of this (the person clicking the link may not be who requested the change, e.g.
      // an admin changed it on their behalf). refreshAuth() below reflects the real session state.
    } catch (e: any) {
      console.error('Error confirming email:', e)
      showError(e, 'Error confirming email')
    } finally {
      await _checkAuthStatus()
      setIsAuthenticating(false)
    }
  }, [code, email, showError, showSuccess, userId, _checkAuthStatus])

  const _confirmNewSecondaryEmail = useCallback(async () => {
    try {
      setIsAuthenticating(true)
      if (!userId || !email || !code) return

      const response = await ProfileService.postApiProfileConfirmSecondaryEmail(
        {
          userId,
          email,
          code,
        } as ConfirmEmailRequest
      )
      showSuccess('Thank you, your email has been confirmed.')
      setUser(response)
      // TODO: Use buildPath for type-safe routes
    } catch (e: any) {
      console.error('Error confirming email:', e)
      showError(e, 'Error confirming email')
    } finally {
      await _checkAuthStatus()
      setIsAuthenticating(false)
    }
  }, [code, email, showError, showSuccess, _checkAuthStatus, userId])

  // Auto logout if 401 response returned from api
  useEffect(() => {
    const interceptor = axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        if (error.response && error.response.status === 401) setUser(null)
        return Promise.reject(error)
      }
    )

    // Clean up the interceptor when the component unmounts
    return () => {
      axiosInstance.interceptors.response.eject(interceptor)
    }
  }, [])

  useEffect(() => {
    _checkAuthStatus()
  }, [_checkAuthStatus])

  const isAuthenticated = !!user

  return (
    <AuthContext
      value={{
        user,
        isLoading: isAuthenticating,
        isAuthenticated,
        login: _login,
        logout: _logout,
        refreshAuth: _checkAuthStatus,
        confirmEmail: _confirmEmail,
        confirmNewEmail: _confirmNewEmail,
        confirmNewSecondaryEmail: _confirmNewSecondaryEmail,
      }}
    >
      {children}
    </AuthContext>
  )
}

export default AuthProvider
